import React, { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Maximize2,
  Minimize2,
  Zap,
  Activity,
  Layers,
  Sparkles,
} from "lucide-react";
import { usePoll } from "../lib/api";
import { buildDurableEquityPoints, EQUITY_TIMEFRAMES } from "./equityChartUtils";

export default function EquityChart({ selectedViewAccountId = "paper", status: accountStatus = {} }) {
  const [timeframe, setTimeframe] = useState("ALL");
  const [showPeakLine, setShowPeakLine] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  // Status comes from the shared control-room source; history is independently durable.
  const accountPathId = encodeURIComponent(selectedViewAccountId);
  const dataQuery = usePoll(`/accounts/${accountPathId}/equity-history?limit=10000`, 15000, []);

  const statusData = accountStatus || {};
  const equityValue = Number(statusData.equity ?? statusData.account?.wallet_balance);
  const currentEquity = Number.isFinite(equityValue) ? equityValue : null;
  const chartPoints = useMemo(
    () => buildDurableEquityPoints(dataQuery.data, timeframe),
    [dataQuery.data, timeframe]
  );

  // Derived statistics
  const stats = useMemo(() => {
    if (!chartPoints.length) {
      return {
        peak: currentEquity ?? 0,
        low: currentEquity ?? 0,
        change: 0,
        changePct: 0,
        isUp: true,
        maxDrawdown: 0,
      };
    }
    let peak = -Infinity;
    let low = Infinity;
    chartPoints.forEach((p) => {
      if (p.equity > peak) peak = p.equity;
      if (p.equity < low) low = p.equity;
    });

    const startVal = chartPoints[0].equity;
    const endVal = chartPoints[chartPoints.length - 1].equity;
    const change = endVal - startVal;
    const changePct = startVal ? (change / startVal) * 100 : 0;
    const maxDrawdown = peak > 0 ? ((peak - low) / peak) * 100 : 0;

    return {
      peak: Number(peak.toFixed(2)),
      low: Number(low.toFixed(2)),
      change: Number(change.toFixed(2)),
      changePct: Number(changePct.toFixed(2)),
      isUp: change >= 0,
      maxDrawdown: Number(maxDrawdown.toFixed(2)),
    };
  }, [chartPoints, currentEquity]);

  const strokeColor = stats.isUp ? "#10b981" : "#f43f5e";

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const pnlFromStart = data.equity - (chartPoints[0]?.equity || data.equity);
      const pnlPct = chartPoints[0]?.equity ? (pnlFromStart / chartPoints[0].equity) * 100 : 0;

      return (
        <div className="bg-zinc-950/95 border border-zinc-700/80 p-3 rounded-lg shadow-2xl font-mono text-xs space-y-1.5 backdrop-blur-md">
          <div className="flex items-center justify-between gap-3 text-zinc-400 text-[10px] border-b border-zinc-800/80 pb-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-amber-300 font-bold">🇮🇳 {data.istDateTime || data.fullTime}</span>
              {data.utcTime && <span className="text-[9px] text-zinc-500 font-mono bg-zinc-900 px-1 py-0.5 rounded border border-zinc-800">UTC {data.utcTime}</span>}
            </div>
            <span className="text-emerald-400 font-bold flex items-center gap-1 text-[9px] bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-800/60">
              <Zap size={9} /> DURABLE
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-zinc-400">Portfolio Equity:</span>
            <b className="text-zinc-100 font-bold text-sm">${data.equity.toLocaleString("en-US", { minimumFractionDigits: 2 })}</b>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-zinc-400">Period PnL:</span>
            <span className={`font-semibold ${pnlFromStart >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {pnlFromStart >= 0 ? "+" : ""}${pnlFromStart.toFixed(2)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%)
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 text-[10px] text-zinc-500 pt-0.5">
            <span>Peak High:</span>
            <span className="text-amber-400 font-mono">${stats.peak}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const chartContent = (
    <div className="flex flex-col h-full bg-zinc-950/90 rounded-lg border border-zinc-800/80 overflow-hidden shadow-xl">
      {/* Top Header Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-zinc-900/80 border-b border-zinc-800/80 text-xs">
        <div className="flex items-center gap-2">
          <div className="p-1 bg-emerald-500/10 rounded border border-emerald-500/20">
            <Activity size={14} className="text-emerald-400" />
          </div>
          <span className="font-bold text-xs tracking-wider text-zinc-200 uppercase font-mono flex items-center gap-1.5">
            Equity Curve
            <span className="px-1.5 py-0.2 bg-sky-950 text-sky-300 border border-sky-800 text-[9px] rounded font-semibold flex items-center gap-1">
              <Sparkles size={9} /> DURABLE DATA
            </span>
          </span>
        </div>

        {/* Action Controls & Timeframe Selector */}
        <div className="flex items-center gap-1.5">
          <div className="flex items-center bg-zinc-900 rounded border border-zinc-800 p-0.5">
            {EQUITY_TIMEFRAMES.map((tf) => (
              <button
                key={tf.id}
                onClick={() => setTimeframe(tf.id)}
                className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded transition ${
                  timeframe === tf.id
                    ? "bg-emerald-500 text-black font-bold shadow-sm"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowPeakLine(!showPeakLine)}
            className={`px-2 py-0.5 rounded text-[10px] font-mono font-medium border transition flex items-center gap-1 ${
              showPeakLine
                ? "bg-amber-950/50 text-amber-300 border-amber-800/60"
                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200"
            }`}
            title="Toggle Peak High Reference Line"
          >
            <Layers size={11} /> Peak
          </button>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition"
            title={isExpanded ? "Minimize Chart" : "Maximize Full-Screen"}
          >
            {isExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>

      {/* Metrics Banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3.5 py-2 bg-black/60 border-b border-zinc-800/40 text-xs font-mono">
        <div>
          <span className="text-[10px] text-zinc-500 uppercase block">Current Equity</span>
          <div className="flex items-center gap-1.5">
            <b className="text-zinc-100 font-bold text-sm">
              {currentEquity === null
                ? "—"
                : `$${currentEquity.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
            </b>
            <span
              className={`text-[10px] font-bold px-1 rounded flex items-center ${
                stats.isUp ? "text-emerald-400 bg-emerald-950/60" : "text-rose-400 bg-rose-950/60"
              }`}
            >
              {stats.isUp ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
              {stats.changePct >= 0 ? "+" : ""}
              {stats.changePct}%
            </span>
          </div>
        </div>

        <div>
          <span className="text-[10px] text-zinc-500 uppercase block">Period Peak High</span>
          <b className="text-amber-400 font-bold text-sm">${stats.peak.toLocaleString()}</b>
        </div>

        <div>
          <span className="text-[10px] text-zinc-500 uppercase block">Max Drawdown</span>
          <b className="text-rose-400 font-bold text-sm">-{stats.maxDrawdown}%</b>
        </div>

        <div>
          <span className="text-[10px] text-zinc-500 uppercase block">Durable Samples</span>
          <div className="flex items-center gap-1">
            <b className="text-sky-400 font-bold text-sm">{chartPoints.length} Points</b>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
          </div>
        </div>
      </div>

      {/* Recharts Area Container */}
      <div className={`flex-1 p-2 bg-black relative ${isExpanded ? "min-h-[450px]" : "h-[180px]"}`}>
        {chartPoints.length ? <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartPoints} margin={{ top: 12, right: 12, bottom: 4, left: 10 }}>
            <defs>
              <linearGradient id="advancedEqGlow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={strokeColor} stopOpacity={0.35} />
                <stop offset="60%" stopColor={strokeColor} stopOpacity={0.08} />
                <stop offset="100%" stopColor={strokeColor} stopOpacity={0.0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" opacity={0.5} vertical={false} />

            <XAxis
              dataKey="time"
              tick={{ fontSize: 9, fill: "#71717a", fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={{ stroke: "#27272a" }}
              minTickGap={35}
            />

            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 9, fill: "#71717a", fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={false}
              orientation="right"
              width={55}
              tickFormatter={(v) => `$${v}`}
            />

            <Tooltip content={<CustomTooltip />} />

            {showPeakLine && stats.peak > 0 && (
              <ReferenceLine
                y={stats.peak}
                stroke="#f59e0b"
                strokeDasharray="4 4"
                strokeWidth={1}
                label={{
                  value: `PEAK $${stats.peak}`,
                  fill: "#f59e0b",
                  fontSize: 9,
                  position: "top",
                  fontFamily: "JetBrains Mono",
                }}
              />
            )}

            <Area
              type="monotone"
              dataKey="equity"
              stroke={strokeColor}
              strokeWidth={2}
              fill="url(#advancedEqGlow)"
              isAnimationActive={true}
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer> : <div className="h-full flex flex-col items-center justify-center gap-2 text-center text-xs font-mono text-zinc-500">
          <Activity size={18} className="text-zinc-600" />
          <span>No durable equity snapshots in this period.</span>
          <span className="text-[10px] text-zinc-600">The first verified sample is recorded by the active runtime.</span>
        </div>}
      </div>
    </div>
  );

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
        <div className="w-full max-w-5xl h-[85vh]">{chartContent}</div>
      </div>
    );
  }

  return chartContent;
}
